"use client";

import type { PropsWithChildren } from "react";
import { InscalarProvider } from "@ilg-core/inscalar-web/react";
import type {
  InscalarConfig,
  LogAdapter,
  Environment,
} from "@ilg-core/inscalar-web";
import datadog from "@ilg-core/analytics/datadog";
import { env } from "next-runtime-env";
import { isProductionEnv } from "@/utilities/helpers";

const inscallarProjectId = env("NEXT_PUBLIC_INSCALAR_PROJECT_ID") ?? "";
const inscallarApiBaseUrl = env("NEXT_PUBLIC_INSCALAR_API_BASE_URL") ?? "";
const inscallarApiKey = env("NEXT_PUBLIC_INSCALAR_API_KEY") ?? "";

const logAdapter: LogAdapter = {
  debug: (message, context) => {
    console.debug(message);
    datadog.log.debug(`[Inscalar] ${message}`, context);
  },
  info: (message, context) => {
    console.info(message);
    datadog.log.info(`[Inscalar] ${message}`, context);
  },
  warn: (message, context) => {
    console.warn(message);
    datadog.log.warn(`[Inscalar] ${message}`, context);
  },
  error: (message, context) => {
    const { error, ...rest } = context ?? {};
    const cause = toError(error);
    // If the value couldn't be turned into an Error, keep it in the context
    // so Datadog still receives it instead of dropping it.
    datadog.log.error(`[Inscalar] ${message}`,
      cause ? { ...rest, error: cause } : (context ?? {}),
    );
  },
};

function toError(serialized: unknown): Error | undefined {
  if (serialized instanceof Error) return serialized;
  if (!serialized || typeof serialized !== "object") return undefined;
  const { name, message, stack } = serialized as {
    name?: string;
    message?: string;
    stack?: string;
  };
  const error = new Error(message ?? "Unknown error");
  if (name) error.name = name;
  if (stack) error.stack = stack;
  return error;
}

const inscalarConfig: InscalarConfig = {
  projectId: inscallarProjectId,
  apiKey: inscallarApiKey,
  environment: (isProductionEnv() ? "prod" : "test") as Environment,
  apiBaseUrl: inscallarApiBaseUrl,
  logAdapter: logAdapter,
  debug: !isProductionEnv(),
};

export default function InscalarWrapper({ children }: PropsWithChildren) {
  return (
    <InscalarProvider config={inscalarConfig}>{children}</InscalarProvider>
  );
}
